// Replay viewer UI overlay: timeline, play/pause, speed, camera, highlights

import {
  ReplayData,
  ReplayState,
  ReplaySpeed,
  CameraMode,
  HighlightMoment,
} from './ReplayTypes';
import { WIZARD_CONFIGS, WizardType } from '../wizards/WizardTypes';

const SPEEDS: ReplaySpeed[] = [0.5, 1, 2, 4];

export class ReplayUI {
  private container: HTMLElement;
  private data: ReplayData;
  private state: ReplayState;

  // UI elements
  private overlay: HTMLElement | null = null;
  private timelineBar: HTMLElement | null = null;
  private timelineProgress: HTMLElement | null = null;
  private timelineCursor: HTMLElement | null = null;
  private timeLabel: HTMLElement | null = null;
  private playPauseBtn: HTMLElement | null = null;
  private speedBtn: HTMLElement | null = null;
  private cameraBtn: HTMLElement | null = null;
  private p1HealthBar: HTMLElement | null = null;
  private p2HealthBar: HTMLElement | null = null;
  private p1HealthText: HTMLElement | null = null;
  private p2HealthText: HTMLElement | null = null;
  private highlightBanner: HTMLElement | null = null;
  private highlightBannerTimeout: ReturnType<typeof setTimeout> | null = null;

  // Callbacks
  private _onPlayPause: (() => void) | null = null;
  private _onSpeedChange: ((speed: ReplaySpeed) => void) | null = null;
  private _onSeek: ((timeMs: number) => void) | null = null;
  private _onCameraChange: ((mode: CameraMode) => void) | null = null;
  private _onWatchHighlights: (() => void) | null = null;
  private _onJumpToHighlight: ((index: number) => void) | null = null;
  private _onExit: (() => void) | null = null;

  constructor(data: ReplayData, state: ReplayState) {
    this.container = document.getElementById('ui-overlay')!;
    this.data = data;
    this.state = state;
  }

  onPlayPause(cb: () => void): void { this._onPlayPause = cb; }
  onSpeedChange(cb: (speed: ReplaySpeed) => void): void { this._onSpeedChange = cb; }
  onSeek(cb: (timeMs: number) => void): void { this._onSeek = cb; }
  onCameraChange(cb: (mode: CameraMode) => void): void { this._onCameraChange = cb; }
  onWatchHighlights(cb: () => void): void { this._onWatchHighlights = cb; }
  onJumpToHighlight(cb: (index: number) => void): void { this._onJumpToHighlight = cb; }
  onExit(cb: () => void): void { this._onExit = cb; }

  show(): void {
    this.createOverlay();
  }

  hide(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  updateState(state: ReplayState): void {
    this.state = state;

    if (this.playPauseBtn) {
      this.playPauseBtn.textContent = state.isPlaying ? 'Pause' : 'Play';
    }
    if (this.speedBtn) {
      this.speedBtn.textContent = `${state.speed}x`;
    }
    if (this.cameraBtn) {
      const labels: Record<CameraMode, string> = {
        free: 'Free Cam',
        player1: this.data.player1Name,
        player2: this.data.player2Name,
      };
      this.cameraBtn.textContent = labels[state.cameraMode];
    }
  }

  updateTimeline(currentTimeMs: number): void {
    if (!this.timelineProgress || !this.timelineCursor || !this.timeLabel) return;
    const pct = Math.min(100, (currentTimeMs / this.data.duration) * 100);
    this.timelineProgress.style.width = `${pct}%`;
    this.timelineCursor.style.left = `${pct}%`;

    const sec = Math.floor(currentTimeMs / 1000);
    const totalSec = Math.floor(this.data.duration / 1000);
    this.timeLabel.textContent = `${this.formatTime(sec)} / ${this.formatTime(totalSec)}`;
  }

  updateHealth(p1: number, p2: number): void {
    if (this.p1HealthBar) {
      this.p1HealthBar.style.width = `${Math.max(0, p1)}%`;
      this.p1HealthBar.style.background =
        p1 < 20 ? '#FF4444' : p1 < 50 ? '#FFA500' : '#44FF44';
    }
    if (this.p2HealthBar) {
      this.p2HealthBar.style.width = `${Math.max(0, p2)}%`;
      this.p2HealthBar.style.background =
        p2 < 20 ? '#FF4444' : p2 < 50 ? '#FFA500' : '#44FF44';
    }
    if (this.p1HealthText) this.p1HealthText.textContent = `${Math.max(0, Math.round(p1))}`;
    if (this.p2HealthText) this.p2HealthText.textContent = `${Math.max(0, Math.round(p2))}`;
  }

  showHighlightBanner(highlight: HighlightMoment): void {
    if (this.highlightBannerTimeout) {
      clearTimeout(this.highlightBannerTimeout);
    }
    if (!this.highlightBanner) return;

    this.highlightBanner.innerHTML = `
      <div style="font-size: 1.6rem; color: #FFD700; margin-bottom: 4px;">${highlight.label}</div>
      <div style="font-size: 0.9rem; color: #E8DCC4;">${highlight.description}</div>
    `;
    this.highlightBanner.style.display = 'flex';
    this.highlightBanner.style.opacity = '1';

    this.highlightBannerTimeout = setTimeout(() => {
      if (this.highlightBanner) {
        this.highlightBanner.style.opacity = '0';
        setTimeout(() => {
          if (this.highlightBanner) this.highlightBanner.style.display = 'none';
        }, 500);
      }
    }, 3000);
  }

  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.id = 'replay-overlay';
    this.overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      pointer-events: none; z-index: 100; font-family: 'Cinzel', serif;
    `;

    // Top bar: player names, wizards, health bars
    const topBar = document.createElement('div');
    topBar.style.cssText = `
      position: absolute; top: 0; left: 0; width: 100%;
      display: flex; justify-content: center; align-items: center;
      padding: 12px 20px; box-sizing: border-box;
      background: linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%);
      pointer-events: auto; gap: 20px;
    `;

    const p1Config = WIZARD_CONFIGS[this.data.player1Wizard as WizardType];
    const p2Config = WIZARD_CONFIGS[this.data.player2Wizard as WizardType];

    topBar.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; flex: 1; justify-content: flex-end;">
        <span style="color: #44FF44; font-size: 1rem;">${this.data.player1Name}</span>
        <span style="font-size: 1.2rem;">${p1Config?.icon || ''}</span>
        <div style="width: 150px; height: 16px; background: rgba(0,0,0,0.5); border-radius: 8px; overflow: hidden; position: relative;">
          <div id="replay-p1-health" style="width: 100%; height: 100%; background: #44FF44; transition: width 0.3s; border-radius: 8px;"></div>
          <span id="replay-p1-health-text" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 0.7rem; color: white; text-shadow: 0 0 3px black;">100</span>
        </div>
      </div>
      <div style="color: #C9A227; font-size: 1.2rem; padding: 0 12px;">VS</div>
      <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
        <div style="width: 150px; height: 16px; background: rgba(0,0,0,0.5); border-radius: 8px; overflow: hidden; position: relative;">
          <div id="replay-p2-health" style="width: 100%; height: 100%; background: #44FF44; transition: width 0.3s; border-radius: 8px;"></div>
          <span id="replay-p2-health-text" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 0.7rem; color: white; text-shadow: 0 0 3px black;">100</span>
        </div>
        <span style="font-size: 1.2rem;">${p2Config?.icon || ''}</span>
        <span style="color: #FF4444; font-size: 1rem;">${this.data.player2Name}</span>
      </div>
    `;
    this.overlay.appendChild(topBar);

    // REPLAY badge
    const badge = document.createElement('div');
    badge.style.cssText = `
      position: absolute; top: 60px; left: 50%; transform: translateX(-50%);
      background: rgba(201, 162, 39, 0.2); border: 1px solid #C9A227;
      color: #C9A227; padding: 4px 16px; border-radius: 4px;
      font-size: 0.85rem; letter-spacing: 2px; pointer-events: none;
    `;
    badge.textContent = 'REPLAY';
    this.overlay.appendChild(badge);

    // Highlight banner (hidden by default)
    this.highlightBanner = document.createElement('div');
    this.highlightBanner.style.cssText = `
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.85); border: 2px solid #FFD700;
      padding: 16px 32px; border-radius: 8px;
      display: none; flex-direction: column; align-items: center;
      pointer-events: none; opacity: 0; transition: opacity 0.5s;
    `;
    this.overlay.appendChild(this.highlightBanner);

    // Bottom controls bar
    const bottomBar = document.createElement('div');
    bottomBar.style.cssText = `
      position: absolute; bottom: 0; left: 0; width: 100%;
      padding: 12px 20px; box-sizing: border-box;
      background: linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%);
      pointer-events: auto;
    `;

    // Timeline
    const timelineRow = document.createElement('div');
    timelineRow.style.cssText = `
      display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
    `;

    this.timeLabel = document.createElement('span');
    this.timeLabel.style.cssText = `color: #E8DCC4; font-size: 0.8rem; min-width: 110px; text-align: center;`;
    this.timeLabel.textContent = `0:00 / ${this.formatTime(Math.floor(this.data.duration / 1000))}`;

    this.timelineBar = document.createElement('div');
    this.timelineBar.style.cssText = `
      flex: 1; height: 8px; background: rgba(255,255,255,0.15);
      border-radius: 4px; cursor: pointer; position: relative;
    `;

    this.timelineProgress = document.createElement('div');
    this.timelineProgress.style.cssText = `
      height: 100%; width: 0%; background: #C9A227; border-radius: 4px;
      transition: width 0.05s linear;
    `;
    this.timelineBar.appendChild(this.timelineProgress);

    this.timelineCursor = document.createElement('div');
    this.timelineCursor.style.cssText = `
      position: absolute; top: 50%; left: 0%; transform: translate(-50%, -50%);
      width: 14px; height: 14px; background: #C9A227; border-radius: 50%;
      border: 2px solid white; box-shadow: 0 0 6px rgba(201,162,39,0.5);
    `;
    this.timelineBar.appendChild(this.timelineCursor);

    // Add highlight markers to timeline
    for (let i = 0; i < this.state.highlights.length; i++) {
      const h = this.state.highlights[i];
      const pct = (h.timestamp / this.data.duration) * 100;
      const marker = document.createElement('div');
      marker.style.cssText = `
        position: absolute; top: -4px; left: ${pct}%;
        transform: translateX(-50%); cursor: pointer;
        font-size: 12px; line-height: 1; z-index: 2;
      `;
      marker.textContent = '\u2605';
      marker.title = h.label;
      marker.style.color = '#FFD700';
      const idx = i;
      marker.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this._onJumpToHighlight) this._onJumpToHighlight(idx);
      });
      this.timelineBar.appendChild(marker);
    }

    // Timeline click-to-seek
    this.timelineBar.addEventListener('click', (e) => {
      const rect = this.timelineBar!.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;
      const targetTime = pct * this.data.duration;
      if (this._onSeek) this._onSeek(targetTime);
    });

    timelineRow.appendChild(this.timeLabel);
    timelineRow.appendChild(this.timelineBar);
    bottomBar.appendChild(timelineRow);

    // Controls row
    const controlsRow = document.createElement('div');
    controlsRow.style.cssText = `
      display: flex; align-items: center; justify-content: center; gap: 12px;
    `;

    // Exit button
    const exitBtn = this.createButton('Exit', () => {
      if (this._onExit) this._onExit();
    });
    exitBtn.style.background = 'rgba(180,40,40,0.4)';
    exitBtn.style.borderColor = '#FF6666';
    exitBtn.style.color = '#FF6666';
    controlsRow.appendChild(exitBtn);

    // Play/Pause
    this.playPauseBtn = this.createButton('Play', () => {
      if (this._onPlayPause) this._onPlayPause();
    });
    controlsRow.appendChild(this.playPauseBtn);

    // Speed
    this.speedBtn = this.createButton('1x', () => {
      const currentIdx = SPEEDS.indexOf(this.state.speed);
      const nextIdx = (currentIdx + 1) % SPEEDS.length;
      if (this._onSpeedChange) this._onSpeedChange(SPEEDS[nextIdx]);
    });
    controlsRow.appendChild(this.speedBtn);

    // Camera mode
    this.cameraBtn = this.createButton('Free Cam', () => {
      const modes: CameraMode[] = ['free', 'player1', 'player2'];
      const currentIdx = modes.indexOf(this.state.cameraMode);
      const nextIdx = (currentIdx + 1) % modes.length;
      if (this._onCameraChange) this._onCameraChange(modes[nextIdx]);
    });
    controlsRow.appendChild(this.cameraBtn);

    // Highlights button (only if highlights exist)
    if (this.state.highlights.length > 0) {
      const highlightsBtn = this.createButton(
        `Highlights (${this.state.highlights.length})`,
        () => {
          if (this._onWatchHighlights) this._onWatchHighlights();
        }
      );
      highlightsBtn.style.borderColor = '#FFD700';
      highlightsBtn.style.color = '#FFD700';
      controlsRow.appendChild(highlightsBtn);
    }

    bottomBar.appendChild(controlsRow);
    this.overlay.appendChild(bottomBar);

    this.container.appendChild(this.overlay);

    // Cache health bar references
    this.p1HealthBar = this.overlay.querySelector('#replay-p1-health');
    this.p2HealthBar = this.overlay.querySelector('#replay-p2-health');
    this.p1HealthText = this.overlay.querySelector('#replay-p1-health-text');
    this.p2HealthText = this.overlay.querySelector('#replay-p2-health-text');
  }

  private createButton(label: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.style.cssText = `
      font-family: 'Cinzel', serif; font-size: 0.85rem;
      padding: 6px 16px; cursor: pointer;
      background: rgba(42, 42, 42, 0.8); border: 1px solid #C9A227;
      color: #E8DCC4; border-radius: 4px;
      transition: background 0.2s;
    `;
    btn.addEventListener('mouseenter', () => {
      btn.style.background = 'rgba(201, 162, 39, 0.2)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = 'rgba(42, 42, 42, 0.8)';
    });
    btn.addEventListener('click', onClick);
    return btn;
  }

  private formatTime(totalSeconds: number): string {
    const min = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }
}
